import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { purchaseActions, purchaseProductActions } from '../../action/purchaseActions';
import Swal from 'sweetalert2';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import dayjs from 'dayjs';
import { Calendar } from "feather-icons-react/build/IconComponents";
import PaymentHistory from './paymentHistory';

// Import Ant Design DatePicker correctly
import { DatePicker } from 'antd';


// Custom hook for numeric input handling
const useNumericInput = (initialValue = 0) => {
    const [value, setValue] = useState(initialValue);
    const [displayValue, setDisplayValue] = useState(String(initialValue));

    const handleChange = (e) => {
        const newValue = e.target.value;
        // Allow empty string or valid number pattern
        if (newValue === '' || /^\d*\.?\d*$/.test(newValue)) {
            setDisplayValue(newValue);
        }
    };

    const handleBlur = () => {
        const numValue = displayValue === '' ? 0 : parseFloat(displayValue);
        setValue(numValue);
        setDisplayValue(String(numValue));
        return numValue;
    };

    const handleFocus = () => {
        setDisplayValue(value === 0 ? '' : String(value));
    };

    const setExternalValue = (newValue) => {
        setValue(newValue);
        setDisplayValue(String(newValue));
    };

    return {
        value,
        displayValue,
        handleChange,
        handleBlur,
        handleFocus,
        setExternalValue
    };
};

const AddPurchases = ({ onSuccess, editData = null, onClose }) => {
    const isEditMode = Boolean(editData);
    const formRef = useRef(null);
    
    // Add refs to prevent unnecessary re-renders
    const productSearchRef = useRef(null);
    const productNameInputRef = useRef(null);
    
    // Payment tracking states
    const currentPaymentInput = useNumericInput(0);
    const [paymentDate, setPaymentDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [originalPaidAmount, setOriginalPaidAmount] = useState(0);
    // Add this for debugging
   
    // Initial form state
    const initialFormData = {
        supplier_id: '',
        purchase_date: dayjs().format('YYYY-MM-DD'),
        reference_no: '',
        status: 'pending',
        payment_status: 'unpaid',
        items: [],
        subtotal: 0,
        tax: 0,
        discount: 0,
        shipping: 0,
        grand_total: 0,
        paid_amount: 0,
        due_amount: 0,
        notes: '',
        created_by: 'Admin'
    };

    const initialNewItem = {
        product_id: '',
        product_name: '',
        quantity: 1,
        unit_price: 0,
        purchase_price: 0,
        selling_price: 0,
        discount: 0,
        tax_percent: 0,
        sku: '',
        category_id: '',
        brand_id: '',
        unit_id: '',
        profit_margin: 30,
    };

    const [formData, setFormData] = useState(initialFormData);
    const [suppliers, setSuppliers] = useState([]);
    const [showProductModal, setShowProductModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [taxRates, setTaxRates] = useState([]);
    const [newItem, setNewItem] = useState(initialNewItem);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [units, setUnits] = useState([]);
    const [supplierSelectValue, setSupplierSelectValue] = useState(null);
    
    // Custom numeric input hooks
    const quantityInput = useNumericInput(1);
    const unitPriceInput = useNumericInput(0);
    const discountInput = useNumericInput(0);
    const shippingInput = useNumericInput(0);
    const paidAmountInput = useNumericInput(0);
    const profitMarginInput = useNumericInput(30);
    
    // Sync numeric inputs with newItem state
    useEffect(() => {
        if (newItem.quantity !== quantityInput.value) {
            quantityInput.setExternalValue(newItem.quantity);
        }
    }, [newItem.quantity]);
    
    useEffect(() => {
        if (newItem.unit_price !== unitPriceInput.value) {
            unitPriceInput.setExternalValue(newItem.unit_price);
        }
    }, [newItem.unit_price]);
    
    useEffect(() => {
        if (newItem.discount !== discountInput.value) {
            discountInput.setExternalValue(newItem.discount);
        }
    }, [newItem.discount]);
    
    useEffect(() => {
        if (newItem.profit_margin !== profitMarginInput.value) {
            profitMarginInput.setExternalValue(newItem.profit_margin);
        }
    }, [newItem.profit_margin]);
    
    // Sync form data numeric inputs
    useEffect(() => {
        if (formData.shipping !== shippingInput.value) {
            shippingInput.setExternalValue(formData.shipping);
        }
    }, [formData.shipping]);
    
    useEffect(() => {
        if (formData.paid_amount !== paidAmountInput.value) {
            paidAmountInput.setExternalValue(formData.paid_amount);
        }
    }, [formData.paid_amount]);
    
    // Store original paid amount
    useEffect(() => {
        if (isEditMode) {
            setOriginalPaidAmount(formData.paid_amount);
        } else {
            setOriginalPaidAmount(0);
        }
    }, [isEditMode, formData.paid_amount]);
    
    // Function to close modal properly
    const handleCancel = () => {
        const modalElement = document.getElementById('add-units');
        if (modalElement) {
            if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
                const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                if (modalInstance) {
                    modalInstance.hide();
                } else {
                    const modal = new window.bootstrap.Modal(modalElement);
                    modal.hide();
                }
            }
            else if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
                window.$(modalElement).modal('hide');
            }
            else {
                modalElement.classList.remove('show');
                modalElement.style.display = 'none';
                modalElement.setAttribute('aria-hidden', 'true');
                
                const backdrops = document.querySelectorAll('.modal-backdrop');
                backdrops.forEach(backdrop => backdrop.remove());
                
                document.body.classList.remove('modal-open');
                document.body.style.paddingRight = '';
                document.body.style.overflow = '';
            }
        }
        
        if (onClose) {
            onClose();
        }
    };
    
    const resetAllData = () => {
        console.log('Resetting all form data...');
        setFormData(initialFormData);
        setNewItem(initialNewItem);
        setPaymentHistory([]);
        setPaymentMethod(null);
        currentPaymentInput.setExternalValue(0);
        setOriginalPaidAmount(0);
        
        setSupplierSelectValue(null);
        setShowProductModal(false);
        
        // Reset numeric inputs
        quantityInput.setExternalValue(1);
        unitPriceInput.setExternalValue(0);
        discountInput.setExternalValue(0);
        shippingInput.setExternalValue(0);
        paidAmountInput.setExternalValue(0);
        profitMarginInput.setExternalValue(30);
        
        if (!isEditMode) {
            setFormData(prev => ({
                ...prev,
                reference_no: generateReferenceNo(),
                purchase_date: dayjs().format('YYYY-MM-DD')
            }));
        }
    };
    
    // Add useEffect for modal hidden event
    useEffect(() => {
        const modalElement = document.getElementById('add-units');
        
        if (modalElement) {
            const handleModalHidden = () => {
                console.log('Modal hidden, resetting data...');
                
                if (onClose) {
                    resetAllData();
                    onClose();
                }
            };
            
            const handleModalShow = () => {
                console.log('Modal shown, preparing form...');
                if (!isEditMode) {
                    resetAllData();
                }
            };
            
            modalElement.addEventListener('hidden.bs.modal', handleModalHidden);
            modalElement.addEventListener('show.bs.modal', handleModalShow);
            
            return () => {
                modalElement.removeEventListener('hidden.bs.modal', handleModalHidden);
                modalElement.removeEventListener('show.bs.modal', handleModalShow);
            };
        }
    }, [isEditMode, onClose]);
    
    // Update supplier select value when formData.supplier_id changes
    useEffect(() => {
        if (suppliers.length > 0 && formData.supplier_id) {
            const selectedSupplier = suppliers.find(s => s.value == formData.supplier_id);
            setSupplierSelectValue(selectedSupplier || null);
        } else if (!formData.supplier_id) {
            setSupplierSelectValue(null);
        }
    }, [formData.supplier_id, suppliers]);
    
    // Generate reference number
    const generateReferenceNo = () => {
        const date = new Date();
        const year = date.getFullYear().toString().substr(-2);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        return `PUR${year}${month}${day}${random}`;
    };
    
const loadEditData = useCallback(async () => {
    debugger;
    if (!editData?.id) return;
    
    try {
        setLoading(true);
        const result = await purchaseActions.getPurchase(editData.id);
        debugger;
        if (result.success) {
            const purchase = result.data;
            console.log('Purchase data loaded:', purchase);
            
            // Format items for display
            const formattedItems = (purchase.items || []).map(item => ({
                ...item,
                product_name: item.product_name || '',
                sku: item.sku || 'N/A',
                quantity: parseFloat(item.quantity) || 1,
                unit_price: parseFloat(item.unit_price) || 0,
                purchase_price: parseFloat(item.unit_price) || 0,
                selling_price: parseFloat(item.selling_price) || 0,
                discount: parseFloat(item.discount) || 0,
                tax_percent: parseFloat(item.tax_percent) || 0,
                tax_amount: parseFloat(item.tax_amount) || 0,
                unit_cost: parseFloat(item.unit_cost) || 0,
                total_cost: parseFloat(item.total_cost) || 0,
                product_id: item.product_id || null,
                profit_margin: parseFloat(item.profit_margin) || 30,
                is_from_this_purchase: true
            }));
            
            // Calculate totals from items
            const subtotal = formattedItems.reduce((sum, item) => 
                sum + (item.unit_price * item.quantity), 0);
            const totalDiscount = formattedItems.reduce((sum, item) => 
                sum + item.discount, 0);
            const totalTax = formattedItems.reduce((sum, item) => 
                sum + item.tax_amount, 0);
            const shipping = parseFloat(purchase.shipping) || 0;
            
            const paidAmount = parseFloat(purchase.paid_amount) || 0;
            const grandTotal = parseFloat((subtotal - totalDiscount + totalTax + shipping).toFixed(4));
            const dueAmount = parseFloat((grandTotal - paidAmount).toFixed(4));
            
            let paymentStatus = purchase.payment_status || 'unpaid';
            if (paidAmount === 0) {
                paymentStatus = 'unpaid';
            } else if (paidAmount > 0 && paidAmount < grandTotal) {
                paymentStatus = 'partial';
            } else if (paidAmount >= grandTotal) {
                paymentStatus = 'paid';
            }
            
            // ===== FIXED: Load payment history properly =====
            // Handle payment_history whether it's a JSON string, array, or null
            let loadedPaymentHistory = [];
            
            if (purchase.payment_history) {
                console.log('Raw payment_history:', purchase.payment_history);
                
                // Case 1: It's already an array
                if (Array.isArray(purchase.payment_history)) {
                    loadedPaymentHistory = purchase.payment_history;
                    console.log('Payment history is already an array:', loadedPaymentHistory);
                } 
                // Case 2: It's a JSON string
                else if (typeof purchase.payment_history === 'string') {
                    try {
                        // Try to parse the JSON string
                        const parsed = JSON.parse(purchase.payment_history);
                        loadedPaymentHistory = Array.isArray(parsed) ? parsed : [];
                        console.log('Parsed payment history from JSON:', loadedPaymentHistory);
                    } catch (e) {
                        console.error('Error parsing payment_history JSON:', e);
                        loadedPaymentHistory = [];
                    }
                }
                // Case 3: It's an object but not an array (shouldn't happen, but handle it)
                else if (typeof purchase.payment_history === 'object') {
                    loadedPaymentHistory = [purchase.payment_history];
                    console.log('Payment history is an object, converting to array:', loadedPaymentHistory);
                }
            }
            
            // Set the payment history state
            setPaymentHistory(loadedPaymentHistory);
            console.log('Final payment history set:', loadedPaymentHistory);
            // =================================================
            
            const newFormData = {
                supplier_id: purchase.supplier_id || '',
                purchase_date: purchase.purchase_date || dayjs().format('YYYY-MM-DD'),
                reference_no: purchase.reference_no || '',
                status: purchase.status || 'pending',
                payment_status: paymentStatus,
                items: formattedItems,
                subtotal: parseFloat(subtotal.toFixed(4)),
                tax: parseFloat(totalTax.toFixed(4)),
                discount: parseFloat(totalDiscount.toFixed(4)),
                shipping: shipping,
                grand_total: grandTotal,
                paid_amount: paidAmount,
                due_amount: dueAmount,
                notes: purchase.notes || '',
                created_by: purchase.created_by || 'Admin'
            };
            
            console.log('New form data:', newFormData);
            setFormData(newFormData);
            setOriginalPaidAmount(paidAmount);
            paidAmountInput.setExternalValue(paidAmount);
            
            // Set supplier select value
            if (purchase.supplier_id && suppliers.length > 0) {
                const selectedSupplier = suppliers.find(s => s.value == purchase.supplier_id);
                setSupplierSelectValue(selectedSupplier || null);
            }
        }
    } catch (error) {
        console.error('Error loading edit data:', error);
        Swal.fire('Error', 'Failed to load purchase data', 'error');
    } finally {
        setLoading(false);
    }
}, [editData, suppliers]);
    
    // Fetch initial data
    useEffect(() => {
        resetAllData();
        fetchSuppliers();
        fetchMasterData();
        
        if (!isEditMode) {
            setFormData(prev => ({
                ...prev,
                reference_no: generateReferenceNo(),
                purchase_date: dayjs().format('YYYY-MM-DD')
            }));
        } else {
            loadEditData();
        }
    }, [isEditMode]);
    
    const fetchSuppliers = async () => {
        try {
            const result = await purchaseActions.getSuppliers();
            debugger;
            if (result.success) {
                debugger;
                const formattedSuppliers = result.data.map(supplier => ({
                    value: supplier.name,
                    label: `${supplier.name} (${supplier.phone || 'No phone'})`,
                    ...supplier
                }));
                setSuppliers(formattedSuppliers);
                
                if (isEditMode && formData.supplier_id) {
                    const selectedSupplier = formattedSuppliers.find(s => s.value == formData.supplier_id);
                    setSupplierSelectValue(selectedSupplier || null);
                }
            } else {
                Swal.fire('Warning', result.message || 'No suppliers found', 'warning');
            }
        } catch (error) {
            console.error('Error fetching suppliers:', error);
        }
    };
    
    const fetchMasterData = async () => {
        try {
            const result = await purchaseProductActions.getPurchaseMasterData();
            
            if (result.success) {
                const { categories, brands, units, taxRates } = result.data;
                
                setCategories(categories.map(cat => ({
                    value: cat.id,
                    label: cat.category_name || cat.name
                })));
                
                setBrands(brands.map(brand => ({
                    value: brand.id,
                    label: brand.Brand || brand.brand_name || brand.name
                })));
                
                setUnits(units.map(unit => ({
                    value: unit.id,
                    label: unit.unit_name || unit.name
                })));
                
                setTaxRates(taxRates.map(tax => ({
                    value: tax.id,
                    label: `${tax.tax_name} (${tax.tax_rate}%)`,
                    tax_rate: parseFloat(tax.tax_rate)
                })));
            }
        } catch (error) {
            console.error('Error fetching master data:', error);
        }
    };
    
    const loadProducts = useCallback(async (inputValue) => {
        try {
            if (!inputValue || inputValue.length < 2) {
                return [];
            }
            
            console.log('Searching products with term:', inputValue);
            const result = await purchaseProductActions.searchProducts(inputValue);
            console.log('Search result:', result);
            
            if (result.success && result.data) {
                return result.data.map(product => ({
                    value: product.value || product.id,
                    id: product.id,
                    label: product.label || `${product.product_name} (${product.sku || 'N/A'}) - Purchase: ₹${product.purchase_price || product.cost_price || 0}`,
                    ...product
                }));
            }
            return [];
        } catch (error) {
            console.error('Error searching products:', error);
            return [];
        }
    }, []);

    const calculateSellingPrice = (purchasePrice, profitMargin = 30) => {
        const purchasePriceNum = parseFloat(purchasePrice) || 0;
        const margin = parseFloat(profitMargin) || 30;
        return parseFloat((purchasePriceNum * (1 + margin / 100)).toFixed(4));
    };

    const handleProductSelect = (selectedOption) => {
        if (selectedOption) {
            console.log('Selected product:', selectedOption);
            let taxPercent = selectedOption.tax_rate || 0;
            const purchasePrice = selectedOption.purchase_price || selectedOption.cost_price || 0;
            const existingSellingPrice = selectedOption.selling_price || 0;
            
            const sellingPrice = existingSellingPrice > 0 
                ? existingSellingPrice 
                : calculateSellingPrice(purchasePrice, newItem.profit_margin);
            
            setNewItem(prev => ({
                ...prev,
                product_id: selectedOption.value || selectedOption.id,
                product_name: selectedOption.product_name,
                sku: selectedOption.sku || '',
                unit_price: purchasePrice,
                purchase_price: purchasePrice,
                selling_price: sellingPrice,
                tax_percent: taxPercent,
                category_id: selectedOption.category_id,
                brand_id: selectedOption.brand_id,
                unit_id: selectedOption.unit_id,
                profit_margin: prev.profit_margin || 30
            }));
            
            unitPriceInput.setExternalValue(purchasePrice);
        }
    };
    
    const calculateItemTotals = (item) => {
        const quantity = parseFloat(item.quantity) || 1;
        const unitPrice = parseFloat(item.unit_price) || 0;
        const discountAmount = parseFloat(item.discount) || 0;
        const taxPercent = parseFloat(item.tax_percent) || 0;
        
        const priceBeforeTax = (unitPrice * quantity) - discountAmount;
        const taxAmount = priceBeforeTax * (taxPercent / 100);
        
        const unitCost = quantity > 0 ? 
            (unitPrice - (discountAmount / quantity)) + (taxAmount / quantity) : 
            unitPrice;
        
        const totalCost = unitCost * quantity;
        
        return {
            tax_amount: parseFloat(taxAmount.toFixed(4)),
            unit_cost: parseFloat(unitCost.toFixed(4)),
            total_cost: parseFloat(totalCost.toFixed(4))
        };
    };
    
    const handleAddItem = () => {
        const productName = newItem.product_name.trim();
        if (!productName) {
            Swal.fire('Error', 'Please enter product name', 'error');
            return;
        }
        
        if (!newItem.quantity || parseFloat(newItem.quantity) <= 0) {
            Swal.fire('Error', 'Please enter valid quantity', 'error');
            return;
        }
        
        if (!newItem.unit_price || parseFloat(newItem.unit_price) <= 0) {
            Swal.fire('Error', 'Please enter valid unit price', 'error');
            return;
        }
        
        if (!newItem.product_id && newItem.product_name) {
            setShowProductModal(true);
            return;
        }
        
        const { tax_amount, unit_cost, total_cost } = calculateItemTotals(newItem);
        
        const sellingPrice = newItem.selling_price > 0 
            ? newItem.selling_price 
            : calculateSellingPrice(newItem.unit_price, newItem.profit_margin);
        
        const item = {
            product_id: newItem.product_id || null,
            product_name: productName,
            sku: newItem.sku || '',
            quantity: parseFloat(newItem.quantity) || 1,
            unit_price: parseFloat(newItem.unit_price) || 0,
            purchase_price: parseFloat(newItem.unit_price) || 0,
            selling_price: parseFloat(sellingPrice),
            discount: parseFloat(newItem.discount) || 0,
            tax_percent: parseFloat(newItem.tax_percent) || 0,
            tax_amount: tax_amount,
            unit_cost: unit_cost,
            total_cost: total_cost,
            category_id: newItem.category_id || null,
            brand_id: newItem.brand_id || null,
            profit_margin: parseFloat(newItem.profit_margin) || 30,
            is_new_item: true
        };
        
        setFormData(prev => {
            const newItems = [...prev.items, item];
            const subtotal = newItems.reduce((sum, i) => sum + (i.unit_price * i.quantity), 0);
            const discount = newItems.reduce((sum, i) => sum + i.discount, 0);
            const tax = newItems.reduce((sum, i) => sum + i.tax_amount, 0);
            const grandTotal = subtotal - discount + tax + (parseFloat(prev.shipping) || 0);
            const dueAmount = grandTotal - (parseFloat(prev.paid_amount) || 0);
            
            let paymentStatus = prev.payment_status;
            const paidAmount = parseFloat(prev.paid_amount) || 0;
            
            if (paidAmount === 0) {
                paymentStatus = 'unpaid';
            } else if (paidAmount > 0 && paidAmount < grandTotal) {
                paymentStatus = 'partial';
            } else if (paidAmount >= grandTotal) {
                paymentStatus = 'paid';
            }
            
            return {
                ...prev,
                items: newItems,
                subtotal: subtotal,
                discount: discount,
                tax: tax,
                grand_total: grandTotal,
                due_amount: dueAmount,
                payment_status: paymentStatus
            };
        });
        
        setNewItem({
            ...initialNewItem,
            product_id: newItem.product_id,
            product_name: '',
            sku: '',
            unit_price: 0,
            profit_margin: newItem.profit_margin
        });
        
        quantityInput.setExternalValue(1);
        unitPriceInput.setExternalValue(0);
        discountInput.setExternalValue(0);
        
        setTimeout(() => {
            if (productSearchRef.current) {
                productSearchRef.current.focus();
            }
        }, 100);
    };

    const handleRemoveItem = (index) => {
        Swal.fire({
            title: 'Remove Item?',
            text: 'Are you sure you want to remove this item?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, remove it!'
        }).then((result) => {
            if (result.isConfirmed) {
                setFormData(prev => {
                    const newItems = prev.items.filter((_, i) => i !== index);
                    const subtotal = newItems.reduce((sum, i) => sum + (i.unit_price * i.quantity), 0);
                    const discount = newItems.reduce((sum, i) => sum + i.discount, 0);
                    const tax = newItems.reduce((sum, i) => sum + i.tax_amount, 0);
                    const grandTotal = subtotal - discount + tax + (parseFloat(prev.shipping) || 0);
                    const dueAmount = grandTotal - (parseFloat(prev.paid_amount) || 0);
                    
                    let paymentStatus = prev.payment_status;
                    const paidAmount = parseFloat(prev.paid_amount) || 0;
                    
                    if (paidAmount === 0) {
                        paymentStatus = 'unpaid';
                    } else if (paidAmount > 0 && paidAmount < grandTotal) {
                        paymentStatus = 'partial';
                    } else if (paidAmount >= grandTotal) {
                        paymentStatus = 'paid';
                    }
                    
                    return {
                        ...prev,
                        items: newItems,
                        subtotal: subtotal,
                        discount: discount,
                        tax: tax,
                        grand_total: grandTotal,
                        due_amount: dueAmount,
                        payment_status: paymentStatus
                    };
                });
                
                Swal.fire('Removed!', 'Item has been removed.', 'success');
            }
        });
    };
    
    const handleManualProductInput = (e) => {
        const { name, value } = e.target;
        setNewItem(prev => ({ ...prev, [name]: value }));
    };
    
    const handleFormChange = useCallback((name, value) => {
        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            
            if (name === 'paid_amount' || name === 'shipping') {
                const paidAmount = name === 'paid_amount' ? parseFloat(value) || 0 : prev.paid_amount;
                const shipping = name === 'shipping' ? parseFloat(value) || 0 : prev.shipping;
                
                let grandTotal = prev.grand_total;
                if (name === 'shipping') {
                    grandTotal = parseFloat((prev.subtotal - prev.discount + prev.tax + shipping).toFixed(4));
                    updated.grand_total = grandTotal;
                } else {
                    grandTotal = prev.grand_total;
                }
                
                const dueAmount = parseFloat((grandTotal - paidAmount).toFixed(4));
                
                let paymentStatus = prev.payment_status;
                
                if (paidAmount === 0) {
                    paymentStatus = 'unpaid';
                } else if (paidAmount > 0 && paidAmount < grandTotal) {
                    paymentStatus = 'partial';
                } else if (paidAmount >= grandTotal) {
                    paymentStatus = 'paid';
                }
                
                updated.due_amount = dueAmount;
                updated.payment_status = paymentStatus;
                
                console.log('Payment calculation:', {
                    grandTotal,
                    paidAmount,
                    dueAmount,
                    paymentStatus
                });
            }
            
            return updated;
        });
    }, []);
    
    const handleCurrentPaymentBlur = () => {
        const amount = currentPaymentInput.handleBlur();
        if (amount > formData.due_amount) {
            Swal.fire({
                icon: 'warning',
                title: 'Payment Exceeds Due',
                text: `Payment amount (₹${amount.toFixed(4)}) exceeds due amount (₹${formData.due_amount.toFixed(4)}). Setting to full due.`,
                timer: 2000
            });
            currentPaymentInput.setExternalValue(formData.due_amount);
        }
    };

 const handleAddPayment = () => {
    const paymentAmount = parseFloat(currentPaymentInput.value) || 0;
    
    if (paymentAmount <= 0) {
        Swal.fire('Error', 'Please enter a valid payment amount', 'error');
        return;
    }
    
    if (paymentAmount > formData.due_amount) {
        Swal.fire('Error', 'Payment amount cannot exceed due amount', 'error');
        return;
    }
    
    if (!paymentMethod) {
        Swal.fire('Error', 'Please select payment method', 'error');
        return;
    }
    
    // Get the method value correctly
    const methodValue = paymentMethod.value || paymentMethod.label;
    
    const newPayment = {
        date: paymentDate || dayjs().format('YYYY-MM-DD'),
        amount: paymentAmount,
        method: methodValue,
        reference: `PAY-${Date.now()}`,
        status: 'completed'
    };
    
    console.log('Adding payment:', newPayment);
    
    setPaymentHistory(prev => [...prev, newPayment]);
    
    const newPaidAmount = formData.paid_amount + paymentAmount;
    const newDueAmount = formData.grand_total - newPaidAmount;
    
    setFormData(prev => ({
        ...prev,
        paid_amount: newPaidAmount,
        due_amount: newDueAmount,
        payment_status: newDueAmount === 0 ? 'paid' : 'partial'
    }));
    
    paidAmountInput.setExternalValue(newPaidAmount);
    
    // IMPORTANT: Clear the current payment input after adding
    currentPaymentInput.setExternalValue(0);
    
    // Optionally reset payment method and date for next payment
    // setPaymentMethod(null);
    // setPaymentDate(dayjs().format('YYYY-MM-DD'));
    
    Swal.fire({
        icon: 'success',
        title: 'Payment Added!',
        text: `₹${paymentAmount.toFixed(4)} added successfully`,
        timer: 1500,
        showConfirmButton: false
    });
};
    
    const handleSupplierChange = (option) => {
        const supplierId = option?.value || '';
        setFormData(prev => ({ ...prev, supplier_id: supplierId }));
        setSupplierSelectValue(option);
    };
    
    const handlePurchaseDateChange = (date) => {
        if (date) {
            const formattedDate = dayjs(date).format('YYYY-MM-DD');
            setFormData(prev => ({
                ...prev,
                purchase_date: formattedDate
            }));
        }
    };
    
    const validateForm = () => {
        if (!formData.supplier_id) {
            Swal.fire('Error', 'Please select supplier', 'error');
            return false;
        }
        
        if (!formData.reference_no.trim()) {
            Swal.fire('Error', 'Please enter reference number', 'error');
            return false;
        }
        
        if (!formData.purchase_date) {
            Swal.fire('Error', 'Please select purchase date', 'error');
            return false;
        }
        
        if (formData.items.length === 0) {
            Swal.fire('Error', 'Please add at least one item', 'error');
            return false;
        }
        
        for (let i = 0; i < formData.items.length; i++) {
            const item = formData.items[i];
            
            if (!item.product_name || item.product_name.trim() === '') {
                Swal.fire('Error', `Product name is required for item #${i + 1}`, 'error');
                return false;
            }
            
            if (!item.quantity || parseFloat(item.quantity) <= 0) {
                Swal.fire('Error', `Valid quantity is required for "${item.product_name}"`, 'error');
                return false;
            }
            
            if (!item.unit_price || parseFloat(item.unit_price) <= 0) {
                Swal.fire('Error', `Valid unit price is required for "${item.product_name}"`, 'error');
                return false;
            }
        }
        
        if (formData.paid_amount < 0) {
            Swal.fire('Error', 'Paid amount cannot be negative', 'error');
            return false;
        }
        
        if (formData.shipping < 0) {
            Swal.fire('Error', 'Shipping cannot be negative', 'error');
            return false;
        }
        
        if (parseFloat(formData.paid_amount) > parseFloat(formData.grand_total)) {
            Swal.fire('Error', 'Paid amount cannot exceed grand total', 'error');
            return false;
        }
        
        return true;
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        try {
            setLoading(true);
            
            const paidAmount = parseFloat(formData.paid_amount) || 0;
            const grandTotal = parseFloat(formData.grand_total) || 0;
            const dueAmount = parseFloat((grandTotal - paidAmount).toFixed(4));
            
            let finalPaymentStatus = formData.payment_status;
            if (paidAmount === 0) {
                finalPaymentStatus = 'unpaid';
            } else if (paidAmount > 0 && paidAmount < grandTotal) {
                finalPaymentStatus = 'partial';
            } else if (paidAmount >= grandTotal) {
                finalPaymentStatus = 'paid';
            }
            
            const itemsArray = formData.items.map(item => ({
                product_id: item.product_id || null,
                product_name: item.product_name,
                sku: item.sku || '',
                quantity: parseFloat(item.quantity) || 1,
                unit_price: parseFloat(item.unit_price) || 0,
                purchase_price: parseFloat(item.purchase_price) || parseFloat(item.unit_price) || 0,
                selling_price: parseFloat(item.selling_price) || 0,
                discount: parseFloat(item.discount) || 0,
                tax_percent: parseFloat(item.tax_percent) || 0,
                tax_amount: parseFloat(item.tax_amount) || 0,
                unit_cost: parseFloat(item.unit_cost) || 0,
                total_cost: parseFloat(item.total_cost) || 0,
                category_id: item.category_id || null,
                brand_id: item.brand_id || null,
                profit_margin: parseFloat(item.profit_margin) || 30
            }));
            
            const purchaseData = {
                supplier_id: formData.supplier_id,
                purchase_date: formData.purchase_date,
                reference_no: formData.reference_no,
                status: formData.status,
                payment_status: finalPaymentStatus,
                items: itemsArray,
                subtotal: parseFloat(formData.subtotal.toFixed(4)),
                tax: parseFloat(formData.tax.toFixed(4)),
                discount: parseFloat(formData.discount.toFixed(4)),
                shipping: parseFloat(formData.shipping || 0),
                grand_total: grandTotal,
                paid_amount: paidAmount,
                due_amount: dueAmount,
                notes: formData.notes || '',
                created_by: formData.created_by || 'Admin',
                payment_history: paymentHistory
            };
            
            console.log('Submitting purchase data:', purchaseData);
            
            let result;
            
            if (isEditMode) {
                purchaseData.id = editData.id;
                result = await purchaseActions.updatePurchase(editData.id, purchaseData);
            } else {
                result = await purchaseActions.createPurchase(purchaseData);
            }
            
            if (result.success) {
                resetAllData();
                
                await Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: isEditMode ? 'Purchase updated successfully' : 'Purchase created successfully',
                    timer: 1500,
                    showConfirmButton: false
                });
                
                handleCancel();
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                Swal.fire('Error', result.message || `Failed to ${isEditMode ? 'update' : 'create'} purchase`, 'error');
            }
        } catch (error) {
            console.error('Error saving purchase:', error);
            Swal.fire('Error', 'Failed to save purchase: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };
    
    const handleDeletePurchase = async () => {
        if (!isEditMode) return;
        
        Swal.fire({
            title: 'Delete Purchase?',
            text: `Are you sure you want to delete purchase ${formData.reference_no}? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    setLoading(true);
                    
                    const deleteData = {
                        id: editData.id,
                        user_id: 1
                    };
                    
                    const result = await purchaseActions.deletePurchase(deleteData);
                    
                    if (result.success) {
                        Swal.fire({
                            icon: 'success',
                            title: 'Deleted!',
                            text: `Purchase ${formData.reference_no} has been deleted successfully.`,
                            timer: 2000
                        });
                        
                        handleCancel();
                        if (onSuccess) onSuccess();
                    } else {
                        Swal.fire('Error', result.message || 'Failed to delete purchase', 'error');
                    }
                } catch (error) {
                    console.error('Error deleting purchase:', error);
                    Swal.fire('Error', 'Failed to delete purchase: ' + error.message, 'error');
                } finally {
                    setLoading(false);
                }
            }
        });
    };
    
    const paymentStatusOptions = [
        { value: 'unpaid', label: 'Unpaid' },
        { value: 'partial', label: 'Partial' },
        { value: 'paid', label: 'Paid' }
    ];
    
    const statusOptions = [
        { value: 'pending', label: 'Pending', dsc: 'Order is pending approval' },
        { value: 'approved', label: 'Approved', dsc: 'Order has been approved' }, 
        { value: 'received', label: 'Received', dsc: 'Items have been received and added to stock' },
        { value: 'rejected', label: 'Rejected', dsc: 'Order has been rejected' },
        { value: 'returned', label: 'Returned / Completed', dsc: 'Return process completed' },
        { value: 'canceled', label: 'Canceled', dsc: 'Order has been canceled' }
    ];
    
    const handleQuantityBlur = () => {
        const qty = quantityInput.handleBlur();
        setNewItem(prev => ({ ...prev, quantity: qty }));
    };
    
    const handleUnitPriceBlur = () => {
        const price = unitPriceInput.handleBlur();
        const sellingPrice = calculateSellingPrice(price, newItem.profit_margin);
        setNewItem(prev => ({ 
            ...prev, 
            unit_price: price,
            purchase_price: price,
            selling_price: sellingPrice 
        }));
    };
    
    const handleDiscountBlur = () => {
        const discount = discountInput.handleBlur();
        setNewItem(prev => ({ ...prev, discount: discount }));
    };
    
    const handleShippingBlur = () => {
        const shipping = shippingInput.handleBlur();
        handleFormChange('shipping', shipping);
    };
    
    const handleProfitMarginBlur = () => {
        const margin = profitMarginInput.handleBlur();
        const sellingPrice = calculateSellingPrice(newItem.unit_price, margin);
        setNewItem(prev => ({
            ...prev,
            profit_margin: margin,
            selling_price: sellingPrice
        }));
    };
    
const handlePaymentDeleted = (updatedData) => {
    console.log('Payment deleted, updating form with:', updatedData);
    
    setFormData(prev => ({
        ...prev,
        paid_amount: updatedData.paid_amount,
        due_amount: updatedData.due_amount,
        payment_status: updatedData.payment_status
    }));
    
    paidAmountInput.setExternalValue(updatedData.paid_amount);
    
    // IMPORTANT: Refresh payment history from the server
    // This will fetch the updated payment list without the deleted payment
    if (editData?.id) {
        refreshPaymentHistory(editData.id);
    }
    
    // Show a small notification that payment was deleted
    Swal.fire({
        icon: 'info',
        title: 'Payment Deleted',
        text: 'Payment removed. Click "Update Purchase" to save changes.',
        showConfirmButton: false,
        timer: 2000
    });
};

// Add this new function to refresh payment history
const refreshPaymentHistory = async (purchaseId) => {
    try {
        const result = await purchaseActions.getPaymentHistory(purchaseId, 1, 10);
        if (result.success) {
            // Update the paymentHistory state with the fresh data from server
            setPaymentHistory(result.data.payments || []);
            console.log('Payment history refreshed:', result.data.payments);
        }
    } catch (error) {
        console.error('Error refreshing payment history:', error);
    }
};
    
    if (loading && isEditMode) {
        return (
            <div className="modal fade" id="add-units">
                <div className="modal-dialog purchase modal-dialog-centered stock-adjust-modal">
                    <div className="modal-content">
                        <div className="text-center p-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Loading purchase data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <>
            <div className="modal fade" id="add-units">
                <div className="modal-dialog purchase modal-dialog-centered stock-adjust-modal modal-xl">
                    <div className="modal-content">
                        <div className="page-wrapper-new p-0">
                            <div className="content">
                                <div className="modal-header border-0 custom-modal-header">
                                    <div className="page-title">
                                        <h4>{isEditMode ? 'Edit Purchase' : 'Add Purchase'}</h4>
                                        <small className="text-muted">
                                            {isEditMode ? 
                                                `Editing: ${formData.reference_no}` : 
                                                'Auto Product Management Enabled'}
                                        </small>
                                    </div>
                                    <button
                                        type="button"
                                        className="close"
                                        onClick={handleCancel}
                                        aria-label="Close"
                                    >
                                        <span aria-hidden="true">×</span>
                                    </button>
                                </div>
                                <div className="modal-body custom-modal-body">
                                    <form ref={formRef} onSubmit={handleSubmit}>
                                        {/* Basic Information Section */}
                                        <div className="card mb-3">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-0">Basic Information</h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                                        <div className="input-blocks add-product">
                                                            <label>Supplier Name *</label>
                                                            <Select 
                                                                options={suppliers}
                                                                className="select" 
                                                                placeholder="Choose Supplier"
                                                                value={supplierSelectValue}
                                                                onChange={handleSupplierChange}
                                                                required
                                                                isClearable={true}
                                                                key={`supplier-select-${formData.supplier_id || 'empty'}`}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                                        <div className="input-blocks">
                                                            <label>Purchase Date *</label>
                                                            <div className="input-groupicon calender-input">
                                                                <Calendar className="info-img" />
                                                                <DatePicker
                                                                    className="datetimepicker"
                                                                    value={dayjs(formData.purchase_date)}
                                                                    onChange={handlePurchaseDateChange}
                                                                    format="DD-MM-YYYY"
                                                                    placeholder="Choose Date"
                                                                    style={{ width: '100%' }}
                                                                    getPopupContainer={trigger => trigger.parentElement}
                                                                    key={`datepicker-${formData.purchase_date}`}
                                                                />
                                                            </div>
                                                            <small className="text-muted mt-1 d-block">
                                                                Current date: {dayjs(formData.purchase_date).format('DD-MM-YYYY')}
                                                            </small>
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                                        <div className="input-blocks">
                                                            <label>Reference No *</label>
                                                            <input 
                                                                type="text" 
                                                                className="form-control"
                                                                name="reference_no"
                                                                value={formData.reference_no}
                                                                onChange={(e) => handleFormChange('reference_no', e.target.value)}
                                                                required
                                                                disabled={isEditMode}
                                                                key={`reference-${formData.reference_no}`}
                                                            />
                                                            <small className="text-muted">
                                                                {isEditMode ? 'Cannot change reference number' : 'Auto-generated'}
                                                            </small>
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                                        <div className="input-blocks">
                                                            <label>Status *</label>
                                                            <Select 
                                                                options={statusOptions}
                                                                className="select"
                                                                value={statusOptions.find(opt => opt.value === formData.status)}
                                                                onChange={(option) => handleFormChange('status', option.value)}
                                                                key={`status-${formData.status}`}
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                                        <div className="input-blocks">
                                                            <label>Payment Status</label>
                                                            <Select 
                                                                options={paymentStatusOptions}
                                                                className="select"
                                                                value={paymentStatusOptions.find(opt => opt.value === formData.payment_status)}
                                                                onChange={(option) => handleFormChange('payment_status', option.value)}
                                                                key={`payment-status-${formData.payment_status}`}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Add Items Section */}
                                        <div className="card mb-3">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-0">Add Products</h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row g-2">
                                                    <div className="col-lg-12">
                                                        <div className="input-blocks">
                                                            <label>Product Search</label>
                                                            <AsyncSelect
                                                                ref={productSearchRef}
                                                                cacheOptions
                                                                defaultOptions
                                                                loadOptions={loadProducts}
                                                                onChange={handleProductSelect}
                                                                placeholder="Type to search products..."
                                                                noOptionsMessage={({ inputValue }) => 
                                                                    inputValue ? 'No products found. Create new?' : 'Type to search...'
                                                                }
                                                                value={newItem.product_id ? {
                                                                    value: newItem.product_id,
                                                                    label: `${newItem.product_name} (${newItem.sku || 'N/A'})`
                                                                } : null}
                                                                key={`product-search-${newItem.product_id || 'empty'}`}
                                                                isClearable
                                                            />
                                                            <small className="text-muted">
                                                                Search by name or SKU. Products not found will be created automatically.
                                                            </small>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <div className="row g-2 mt-2">
                                                    <div className="col-lg-8">
                                                        <label>Product Name *</label>
                                                        <input
                                                            ref={productNameInputRef}
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Product Name"
                                                            name="product_name"
                                                            value={newItem.product_name}
                                                            onChange={handleManualProductInput}
                                                        />
                                                    </div>
                                                    <div className="col-lg-4">
                                                        <label>SKU</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="SKU"
                                                            name="sku"
                                                            value={newItem.sku}
                                                            onChange={handleManualProductInput}
                                                        />
                                                    </div>
                                                    <div className="col-lg-2">
                                                        <label>Qty *</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Qty"
                                                            value={quantityInput.displayValue}
                                                            onChange={quantityInput.handleChange}
                                                            onBlur={handleQuantityBlur}
                                                            onFocus={quantityInput.handleFocus}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="col-lg-2">
                                                        <label>Purchase Price *</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Purchase Price"
                                                            value={unitPriceInput.displayValue}
                                                            onChange={unitPriceInput.handleChange}
                                                            onBlur={handleUnitPriceBlur}
                                                            onFocus={unitPriceInput.handleFocus}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="col-lg-2">
                                                        <label>Discount</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Discount"
                                                            value={discountInput.displayValue}
                                                            onChange={discountInput.handleChange}
                                                            onBlur={handleDiscountBlur}
                                                            onFocus={discountInput.handleFocus}
                                                        />
                                                    </div>
                                                    <div className="col-lg-2">
                                                        <label>Tax %</label>
                                                        <Select
                                                            options={taxRates}
                                                            placeholder="Tax %"
                                                            value={taxRates.find(t => t.tax_rate == newItem.tax_percent)}
                                                            onChange={(option) => 
                                                                setNewItem(prev => ({ 
                                                                    ...prev, 
                                                                    tax_percent: option?.tax_rate || 0 
                                                                }))
                                                            }
                                                            isClearable
                                                        />
                                                    </div>
                                                    <div className="col-lg-2">
                                                        <label>Profit Margin %</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Profit %"
                                                            value={profitMarginInput.displayValue}
                                                            onChange={profitMarginInput.handleChange}
                                                            onBlur={handleProfitMarginBlur}
                                                            onFocus={profitMarginInput.handleFocus}
                                                        />
                                                    </div>
                                                    <div className="col-lg-1 align-self-end">
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary w-100"
                                                            onClick={handleAddItem}
                                                            disabled={!newItem.product_name.trim() || !newItem.unit_price || parseFloat(newItem.unit_price) <= 0}
                                                        >
                                                            Add
                                                        </button>
                                                    </div>
                                                </div>
                                                
                                                {/* Price Display */}
                                                {newItem.unit_price > 0 && (
                                                    <div className="row mt-2">
                                                        <div className="col-lg-12">
                                                            <div className="alert alert-info py-2 mb-0">
                                                                <small>
                                                                    <strong>Price Breakdown:</strong><br />
                                                                    • Purchase Price: ₹{parseFloat(newItem.unit_price).toFixed(4)}<br />
                                                                    • Profit Margin: {newItem.profit_margin || 30}%<br />
                                                                    • Calculated Selling Price: ₹{newItem.selling_price ? parseFloat(newItem.selling_price).toFixed(4) : calculateSellingPrice(newItem.unit_price, newItem.profit_margin).toFixed(4)}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Items Table */}
                                        {formData.items.length > 0 && (
                                            <div className="card mb-3">
                                                <div className="card-header bg-light">
                                                    <h6 className="mb-0">Product List</h6>
                                                </div>
                                                <div className="card-body p-0">
                                                    <div className="table-responsive">
                                                        <table className="table table-hover table-sm mb-0">
                                                            <thead className="bg-light">
                                                                <tr>
                                                                    <th>#</th>
                                                                    <th>Product</th>
                                                                    <th>SKU</th>
                                                                    <th>Qty</th>
                                                                    <th>Purchase Price</th>
                                                                    <th>Selling Price</th>
                                                                    <th>Discount</th>
                                                                    <th>Tax %</th>
                                                                    <th>Tax Amt</th>
                                                                    <th>Unit Cost</th>
                                                                    <th>Total Cost</th>
                                                                    <th>Action</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {formData.items.map((item, index) => (
                                                                    <tr key={index}>
                                                                        <td>{index + 1}</td>
                                                                        <td>
                                                                            <strong>{item.product_name}</strong>
                                                                            {!item.product_id && (
                                                                                <span className="badge bg-warning ms-1">New</span>
                                                                            )}
                                                                        </td>
                                                                        <td>
                                                                            {item.sku && item.sku !== 'N/A' ? (
                                                                                <span>{item.sku}</span>
                                                                            ) : (
                                                                                <span className="text-muted">-</span>
                                                                            )}
                                                                        </td>
                                                                        <td>{parseFloat(item.quantity).toFixed(4)}</td>
                                                                        <td>₹{parseFloat(item.unit_price).toFixed(4)}</td>
                                                                        <td>
                                                                            ₹{parseFloat(item.selling_price || calculateSellingPrice(item.unit_price, item.profit_margin || 30)).toFixed(4)}
                                                                            <small className="d-block text-muted">
                                                                                Margin: {item.profit_margin || 30}%
                                                                            </small>
                                                                        </td>
                                                                        <td>₹{parseFloat(item.discount).toFixed(4)}</td>
                                                                        <td>{item.tax_percent}%</td>
                                                                        <td>₹{parseFloat(item.tax_amount).toFixed(4)}</td>
                                                                        <td>₹{parseFloat(item.unit_cost).toFixed(4)}</td>
                                                                        <td>₹{parseFloat(item.total_cost).toFixed(4)}</td>
                                                                        <td>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-danger btn-sm"
                                                                                onClick={() => handleRemoveItem(index)}
                                                                            >
                                                                                <i className="fas fa-trash"></i>
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                                <tr className="table-secondary">
                                                                    <td colSpan="10" className="text-end"><strong>Totals:</strong></td>
                                                                    <td><strong>₹{formData.grand_total.toFixed(4)}</strong></td>
                                                                    <td></td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Summary Cards */}
                                        <div className="row mb-3">
                                            <div className="col-lg-3 col-md-6">
                                                <div className="card bg-light">
                                                    <div className="card-body py-2">
                                                        <small className="text-muted">Subtotal</small>
                                                        <h6 className="mb-0">₹{formData.subtotal.toFixed(4)}</h6>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-lg-3 col-md-6">
                                                <div className="card bg-light">
                                                    <div className="card-body py-2">
                                                        <small className="text-muted">Total Tax</small>
                                                        <h6 className="mb-0">₹{formData.tax.toFixed(4)}</h6>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-lg-3 col-md-6">
                                                <div className="card bg-light">
                                                    <div className="card-body py-2">
                                                        <small className="text-muted">Total Discount</small>
                                                        <h6 className="mb-0">₹{formData.discount.toFixed(4)}</h6>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-lg-3 col-md-6">
                                                <div className="card bg-light">
                                                    <div className="card-body py-2">
                                                        <small className="text-muted">Shipping</small>
                                                        <h6 className="mb-0">
                                                            <input 
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                value={shippingInput.displayValue}
                                                                onChange={shippingInput.handleChange}
                                                                onBlur={handleShippingBlur}
                                                                onFocus={shippingInput.handleFocus}
                                                                style={{ width: '100px', display: 'inline-block' }}
                                                            />
                                                        </h6>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Payment Summary Cards */}
                                        <div className="row mb-3">
                                            <div className="col-lg-3 col-md-6">
                                                <div className="card border-primary">
                                                    <div className="card-body py-2">
                                                        <small className="text-primary">Total Purchase Amount</small>
                                                        <h5 className="mb-0 text-primary">₹{formData.grand_total.toFixed(4)}</h5>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-lg-3 col-md-6">
                                                <div className="card border-success">
                                                    <div className="card-body py-2">
                                                        <small className="text-success">Total Paid (History)</small>
                                                        <h5 className="mb-0 text-success">₹{formData.paid_amount.toFixed(4)}</h5>
                                                        <small className="text-muted">Amount paid so far</small>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-lg-3 col-md-6">
                                                <div className="card border-danger">
                                                    <div className="card-body py-2">
                                                        <small className="text-danger">Current Due Amount</small>
                                                        <h5 className="mb-0 text-danger">₹{formData.due_amount.toFixed(4)}</h5>
                                                        <small className="text-muted">Remaining balance</small>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-lg-3 col-md-6">
                                                <div className={`card ${
                                                    formData.payment_status === 'paid' ? 'border-success' :
                                                    formData.payment_status === 'partial' ? 'border-warning' : 'border-danger'
                                                }`}>
                                                    <div className="card-body py-2">
                                                        <small>Payment Status</small>
                                                        <h5 className={`mb-0 ${
                                                            formData.payment_status === 'paid' ? 'text-success' :
                                                            formData.payment_status === 'partial' ? 'text-warning' : 'text-danger'
                                                        }`}>
                                                            {formData.payment_status.toUpperCase()}
                                                        </h5>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Payment Entry Section - Available in BOTH Add and Edit Modes */}
                                        <div className="card border-primary mb-3">
                                            <div className="card-header bg-primary text-white">
                                                <h6 className="mb-0">
                                                    <i className="fas fa-money-bill-wave me-2"></i>
                                                    {isEditMode ? 'Make a Payment' : 'Initial Payment (Optional)'}
                                                </h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row align-items-end">
                                                    <div className="col-lg-4">
                                                        <div className="input-blocks">
                                                            <label className="text-primary">
                                                                <strong>Payment Amount</strong>
                                                            </label>
                                                            <div className="input-group">
                                                                <span className="input-group-text bg-primary text-white">₹</span>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Enter payment amount"
                                                                    value={currentPaymentInput.displayValue}
                                                                    onChange={currentPaymentInput.handleChange}
                                                                    onBlur={handleCurrentPaymentBlur}
                                                                    onFocus={currentPaymentInput.handleFocus}
                                                                />
                                                            </div>
                                                            <small className="text-muted">
                                                                {isEditMode ? 'Enter additional payment amount' : 'Enter initial payment amount'}
                                                            </small>
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-3">
                                                        <div className="input-blocks">
                                                            <label>Payment Date</label>
                                                            <DatePicker
                                                                className="form-control"
                                                                value={paymentDate ? dayjs(paymentDate) : null}
                                                                onChange={(date) => setPaymentDate(date ? date.format('YYYY-MM-DD') : null)}
                                                                format="DD-MM-YYYY"
                                                                placeholder="Select date"
                                                            />
                                                        </div>
                                                    </div>
                                                   <div className="col-lg-3">
    <div className="input-blocks">
        <label>Payment Method <span className="text-danger">*</span></label>
        <Select
            options={[
                { value: 'cash', label: 'Cash' },
                { value: 'bank_transfer', label: 'Bank Transfer' },
                { value: 'cheque', label: 'Cheque' },
                { value: 'credit_card', label: 'Credit Card' },
                { value: 'upi', label: 'UPI' }
            ]}
            value={paymentMethod}
            onChange={(option) => setPaymentMethod(option)}
            placeholder="Select method"
            isClearable={false}
            // REMOVE the required attribute from here
        />
        <small className="text-muted">Required only when adding payment</small>
    </div>
</div>
                                                    <div className="col-lg-2">
                                                        <button
                                                            type="button"
                                                            className="btn btn-success w-100"
                                                            onClick={handleAddPayment}
                                                            disabled={!currentPaymentInput.value || currentPaymentInput.value <= 0 || !paymentMethod}
                                                        >
                                                            <i className="fas fa-plus-circle me-2"></i>
                                                            {isEditMode ? 'Add Payment' : 'Add Initial Payment'}
                                                        </button>
                                                    </div>
                                                </div>
                                                
                                                {/* Quick Actions */}
{formData.due_amount > 0 && (
    <div className="row mt-2">
        <div className="col-lg-12">
            <div className="d-flex gap-2">
                <button
                    type="button"
                    className="btn btn-outline-success btn-sm"
                    onClick={() => {
                        // Set payment amount to FULL current due
                        currentPaymentInput.setExternalValue(formData.due_amount);
                    }}
                >
                    Pay Full Due (₹{formData.due_amount.toFixed(4)})
                </button>
                <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => {
                        // Set payment amount to HALF current due
                        currentPaymentInput.setExternalValue(formData.due_amount / 2);
                    }}
                >
                    Pay Half (₹{(formData.due_amount / 2).toFixed(4)})
                </button>
            </div>
        </div>
    </div>
)}
                                                
                                              
                                               {/* Payment Preview & Summary */}
<div className="row mt-3">
    <div className="col-lg-12">
        {/* Active Payment Preview - Only show when entering NEW payment */}
        {currentPaymentInput.value > 0 && parseFloat(currentPaymentInput.value) !== paymentHistory[paymentHistory.length - 1]?.amount && (
            <div className="alert alert-info mb-2">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <strong>Payment Preview:</strong><br />
                        • Current Due: ₹{formData.due_amount.toFixed(4)}<br />
                        • Payment Now: ₹{parseFloat(currentPaymentInput.value || 0).toFixed(4)}<br />
                        • New Due After Payment: ₹{(formData.due_amount - parseFloat(currentPaymentInput.value || 0)).toFixed(4)}
                    </div>
                    <span className={`badge ${(formData.due_amount - parseFloat(currentPaymentInput.value || 0)) === 0 ? 'bg-success' : 'bg-warning'} p-2`}>
                        After payment: {(formData.due_amount - parseFloat(currentPaymentInput.value || 0)) === 0 ? 'FULLY PAID' : 'PARTIAL'}
                    </span>
                </div>
            </div>
        )}
        
        {/* Payments Added Summary */}
        {paymentHistory.length > 0 && (
            <div className="alert alert-success">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <strong>Payments Added:</strong>
                        <ul className="list-unstyled mb-0 mt-1">
                            {paymentHistory.map((payment, index) => (
                                <li key={index} className="small">
                                    <i className="fas fa-check-circle text-success me-1"></i>
                                    ₹{payment.amount.toFixed(4)} - {payment.method} - {dayjs(payment.date).format('DD-MM-YYYY')}
                                </li>
                            ))}
                        </ul>
                        <div className="mt-1">
                            <strong>Total Paid Now: ₹{(formData.paid_amount - originalPaidAmount).toFixed(4)}</strong>
                        </div>
                    </div>
                    <span className="badge bg-success p-2">
                        {paymentHistory.length} Payment{paymentHistory.length > 1 ? 's' : ''} Added
                    </span>
                </div>
            </div>
        )}
    </div>
</div>
                                            </div>
                                        </div>
                                        
                                        {/* Payment History - Only in Edit Mode */}
                                        {isEditMode && editData?.id && (
                                            <div className="card mb-3">
                                                <div className="card-header bg-secondary text-white">
                                                    <h6 className="mb-0">
                                                        <i className="fas fa-history me-2"></i>
                                                        Payment History
                                                    </h6>
                                                </div>
                                                <div className="card-body p-0">
                                                    <PaymentHistory 
                                                        purchaseId={editData.id} 
                                                        onPaymentDeleted={handlePaymentDeleted}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Notes Section */}
                                        <div className="card mb-3">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-0">Additional Notes</h6>
                                            </div>
                                            <div className="card-body">
                                                <textarea
                                                    className="form-control"
                                                    name="notes"
                                                    value={formData.notes}
                                                    onChange={(e) => handleFormChange('notes', e.target.value)}
                                                    rows={2}
                                                    placeholder="Any additional notes about this purchase..."
                                                />
                                            </div>
                                        </div>
                                        
                                        {/* Form Actions */}
                                        <div className="row">
                                            <div className="col-lg-12">
                                                <div className="d-flex justify-content-between">
                                                    <div>
                                                        {isEditMode && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-danger me-2"
                                                                onClick={handleDeletePurchase}
                                                                disabled={loading}
                                                            >
                                                                <i className="fas fa-trash me-2"></i>
                                                                {loading ? 'Deleting...' : 'Delete Purchase'}
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary me-2"
                                                            onClick={handleCancel}
                                                            disabled={loading}
                                                        >
                                                            <i className="fas fa-times me-2"></i>
                                                            Cancel
                                                        </button>
                                                        <button 
    type="submit" 
    className="btn btn-primary"
    disabled={formData.items.length === 0 || loading} // Only disabled if no items OR loading
>
    <i className={`fas ${isEditMode ? 'fa-save' : 'fa-check'} me-2`}></i>
    {loading ? 'Saving...' : (isEditMode ? 'Update Purchase' : 'Submit Purchase')}
</button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Product Creation Modal */}
            <Modal show={showProductModal} onHide={() => setShowProductModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Create New Product</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        The product <strong>{newItem.product_name}</strong> was not found in the database.
                        Would you like to create it?
                    </p>
                    
                    <div className="mt-3">
                        <label>SKU (Optional)</label>
                        <div className="input-group mb-3">
                            <input
                                type="text"
                                className="form-control"
                                value={newItem.sku}
                                onChange={(e) => setNewItem(prev => ({ ...prev, sku: e.target.value }))}
                                placeholder="Auto-generate if blank"
                            />
                            <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() => {
                                    const sku = `PUR-${Date.now().toString(36).toUpperCase()}`;
                                    setNewItem(prev => ({ ...prev, sku }));
                                }}
                            >
                                Generate
                            </button>
                        </div>
                        
                        <div className="row g-2">
                            <div className="col-lg-6">
                                <label>Category</label>
                                <Select
                                    options={categories}
                                    placeholder="Select Category"
                                    onChange={(option) => setNewItem(prev => ({ 
                                        ...prev, 
                                        category_id: option?.value || '' 
                                    }))}
                                />
                            </div>
                            <div className="col-lg-6">
                                <label>Brand</label>
                                <Select
                                    options={brands}
                                    placeholder="Select Brand"
                                    onChange={(option) => setNewItem(prev => ({ 
                                        ...prev, 
                                        brand_id: option?.value || '' 
                                    }))}
                                />
                            </div>
                        </div>
                        
                        <div className="row g-2 mt-2">
                            <div className="col-lg-6">
                                <label>Unit</label>
                                <Select
                                    options={units}
                                    placeholder="Select Unit"
                                    onChange={(option) => setNewItem(prev => ({ 
                                        ...prev, 
                                        unit_id: option?.value || '' 
                                    }))}
                                />
                            </div>
                            <div className="col-lg-6">
                                <label>Tax Rate</label>
                                <Select
                                    options={taxRates}
                                    placeholder="Select Tax Rate"
                                    onChange={(option) => setNewItem(prev => ({ 
                                        ...prev, 
                                        tax_percent: option?.tax_rate || 0 
                                    }))}
                                />
                            </div>
                        </div>
                        
                        <div className="row g-2 mt-2">
                            <div className="col-lg-6">
                                <label>Purchase Price</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={unitPriceInput.displayValue}
                                    onChange={unitPriceInput.handleChange}
                                    onBlur={handleUnitPriceBlur}
                                    placeholder="Purchase price"
                                />
                            </div>
                            <div className="col-lg-6">
                                <label>Profit Margin %</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={profitMarginInput.displayValue}
                                    onChange={profitMarginInput.handleChange}
                                    onBlur={handleProfitMarginBlur}
                                    placeholder="30%"
                                />
                            </div>
                        </div>
                        
                        <div className="mt-3">
                            <div className="alert alert-info">
                                <small>
                                    Product will be created with:
                                    <br />
                                    • Name: {newItem.product_name}
                                    <br />
                                    • Purchase Price: ₹{parseFloat(newItem.unit_price).toFixed(4)}
                                    <br />
                                    • Selling Price: ₹{calculateSellingPrice(newItem.unit_price, newItem.profit_margin || 30).toFixed(4)}
                                    <br />
                                    • Profit Margin: {newItem.profit_margin || 30}%
                                    <br />
                                    • Tax Rate: {newItem.tax_percent}%
                                </small>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => {
                        setShowProductModal(false);
                    }} disabled={loading}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={async () => {
                        try {
                            setLoading(true);
                            
                            const itemCode = `ITM-${Date.now().toString(36).toUpperCase()}`;
                            
                            const taxType = taxRates.find(t => t.tax_rate === parseFloat(newItem.tax_percent));
                            
                            const sellingPrice = calculateSellingPrice(newItem.unit_price, newItem.profit_margin || 30);
                            const mrp = parseFloat((sellingPrice * 1.1).toFixed(4));
                            
                            const productData = {
                                product_name: newItem.product_name,
                                sku: newItem.sku || `PUR-${Date.now().toString(36).toUpperCase()}`,
                                item_code: itemCode,
                                category_id: newItem.category_id,
                                brand_id: newItem.brand_id,
                                unit_id: newItem.unit_id,
                                purchase_price: newItem.unit_price,
                                cost_price: newItem.unit_price,
                                selling_price: sellingPrice,
                                mrp: mrp,
                                tax_type_id: taxType?.value || null,
                                status: 'Active',
                                created_by: 1,
                                profit_margin: newItem.profit_margin || 30
                            };
                            
                            console.log('Creating product with data:', productData);
                            const result = await purchaseProductActions.addProductQuick(productData);
                            
                            if (result.success) {
                                console.log('Product created successfully:', result.data);
                                
                                setNewItem(prev => ({
                                    ...prev,
                                    product_id: result.data.id,
                                    sku: result.data.sku,
                                    category_id: result.data.category_id,
                                    brand_id: result.data.brand_id,
                                    unit_id: result.data.unit_id,
                                    tax_percent: result.data.tax_rate || newItem.tax_percent,
                                    selling_price: result.data.selling_price || sellingPrice,
                                    profit_margin: result.data.profit_margin || newItem.profit_margin || 30
                                }));
                                
                                setShowProductModal(false);
                                
                                Swal.fire({
                                    icon: 'success',
                                    title: 'Product Created!',
                                    text: 'Product has been added to the database.',
                                    timer: 1500
                                });
                                
                                setTimeout(() => {
                                    handleAddItem();
                                }, 5000);
                            } else {
                                Swal.fire('Error', result.message || 'Failed to create product', 'error');
                            }
                        } catch (error) {
                            console.error('Error creating product:', error);
                            Swal.fire('Error', 'Failed to create product: ' + error.message, 'error');
                        } finally {
                            setLoading(false);
                        }
                    }} disabled={loading}>
                        {loading ? 'Creating...' : 'Create & Add to Purchase'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

AddPurchases.propTypes = {
    onSuccess: PropTypes.func,
    editData: PropTypes.object,
    onClose: PropTypes.func
};

export default AddPurchases;